import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import ModalContext from '../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import UserSelect from '../../ProductionFlow/GenerateRNCModal/Select/UserSelect'
import SelectAndSearch from '../../SelectAndSearch'
import ApiService from '../../../services/ApiService'
import angleDown from '../../../assets/production-flow/rnc/angledown.svg'
import clip from '../../../assets/production-flow/rnc/clip.svg'
import crossLight from '../../../assets/stock/FiltersHeader/cross.svg'
import closeBig from '../../../assets/production-flow/rnc/closebig.svg'
import switchCheck from '../../../assets/production-flow/rnc/switchcheck.svg'
import plus from '../../../assets/production-flow/rnc/plus.svg'
import trash from '../../../assets/production-flow/rnc/trash.svg'
import trashLight from '../../../assets/production-flow/rnc/trashlight.svg'
import whitePlus from '../../../assets/production-flow/whiteplus.svg'
import { X } from 'lucide-react'

import {
    Container,
    HeaderContainer,
    HeaderTitle,
    CloseButton,
    ContentContainer,
    FormGrid,
    FormGroup,
    Label,
    Input,
    SelectWrapper,
    SelectIcon,
    Select,
    Section,
    CausaPertinenteWrapper,
    CausaWrapper,
    PertinenteWrapper,
    ToggleSwitchLabel,
    ToggleSwitchInput,
    ToggleSwitchSlider,
    ToggleSwitchThumb,
    ActionItemsSection,
    ActionItemsHeader,
    ActionItemRow,
    ResponsibleInputWrapper,
    IconButton,
    AddButton,
    AttachmentContainer,
    FileList,
    FileItem,
    FileRemoveButton,
    ButtonContainer,
    CancelButton,
    SaveButton,
} from './style'
import { Providence } from '../../../pages/ClientPanel/Occurrences/OccurencesTable/types'


interface User {
    id: number
    name: string
}

interface StageOption {
    id: number
    name: string
    sectorId?: number | null
}

interface AreaOption {
    id: number
    name: string
}

interface SectorOption {
    id: number
    name: string
}

const CreateEditOccurrenceModal: React.FC = () => {
    const { setModalActive } = useContext(ModalContext)
    const { occurrence, setOccurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)

    const isEditMode = !!occurrence?.id

    // Form fields
    const [description, setDescription] = useState('')
    const [allocation, setAllocation] = useState('')
    const [quantity, setQuantity] = useState('')
    const [cause, setCause] = useState('')
    const [correction, setCorrection] = useState('')
    const [relevant, setRelevant] = useState(false)
    const [stageId, setStageId] = useState<number | ''>('')

    // Stages
    const [stages, setStages] = useState<StageOption[]>([])
    const [loadingStages, setLoadingStages] = useState(true)
    const [areas, setAreas] = useState<AreaOption[]>([])
    const [sectors, setSectors] = useState<SectorOption[]>([])
    const [selectedAreaId, setSelectedAreaId] = useState<number | ''>('')
    const [selectedSectorId, setSelectedSectorId] = useState<number | ''>('')
    const [loadingAreas, setLoadingAreas] = useState(false)
    const [loadingSectors, setLoadingSectors] = useState(false)

    // Providences
    const [providences, setProvidences] = useState<Providence[]>([])
    const [providenceIdsToDelete, setProvidenceIdsToDelete] = useState<number[]>([])

    // Files
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [existingFiles, setExistingFiles] = useState<{ id: number; name: string; url: string }[]>([])

    // State
    const [isSaving, setIsSaving] = useState(false)

    // Load stages
    useEffect(() => {
        setLoadingStages(true)
        ApiService.stages.findAll()
            .then(response => {
                setStages(response.data || [])
            })
            .catch(error => {
                console.error('Erro ao carregar etapas:', error)
            })
            .finally(() => {
                setLoadingStages(false)
            })
    }, [])

    useEffect(() => {
        setLoadingAreas(true)
        ApiService.services.getAreas({ limit: 200, offset: 0, query: '' })
            .then(response => {
                setAreas(response.data || [])
            })
            .catch(error => {
                console.error('Erro ao carregar áreas:', error)
            })
            .finally(() => {
                setLoadingAreas(false)
            })
    }, [])

    useEffect(() => {
        if (!selectedAreaId) {
            setSectors([])
            setSelectedSectorId('')
            return
        }

        setLoadingSectors(true)
        ApiService.services.getSectors({
            areaId: selectedAreaId,
            limit: 200,
            offset: 0,
            query: '',
        })
            .then(response => {
                setSectors(response.data || [])
            })
            .catch(error => {
                console.error('Erro ao carregar setores:', error)
            })
            .finally(() => {
                setLoadingSectors(false)
            })
    }, [selectedAreaId])

    const filteredStages = useMemo(() => {
        if (!selectedSectorId) return stages
        return stages.filter(stage => Number(stage.sectorId) === Number(selectedSectorId))
    }, [stages, selectedSectorId])

    useEffect(() => {
        if (!stageId) return
        if (loadingStages) return
        const stillExists = filteredStages.some(stage => stage.id === Number(stageId))
        if (!stillExists) {
            setStageId('')
        }
    }, [filteredStages, stageId, loadingStages])

    // serviceId from Production Flow pre-fill
    const [serviceId, setServiceId] = useState<number | null>(null)
    const [selectedService, setSelectedService] = useState<{ id: number; name: string } | null>(null)

    const resolveOperationDisplayName = (occ: any): string | null => {
        const serviceFromOccurrence = occ?.Services || {}
        return (
            serviceFromOccurrence?.codeOfOP ||
            serviceFromOccurrence?.codeOfOp ||
            serviceFromOccurrence?.codeofop ||
            occ?.operationNumber ||
            null
        )
    }

    // Fetch services for search
    const fetchServices = useCallback(async (search: string): Promise<{ id: number; name: string }[]> => {
        try {
            const response = await ApiService.productionFlow.searchServices({ search, limit: 10 })
            const data = response.data?.data || response.data || []
            return data.map((s: any) => ({
                id: s.id,
                name: s.codeOfOP || `OP-${s.id}`,
            }))
        } catch (error) {
            console.error('Erro ao buscar serviços:', error)
            return []
        }
    }, [])

    // Populate form when editing or pre-filled (from Production Flow)
    useEffect(() => {
        if (!occurrence) return

        if (isEditMode) {
            // Edit mode — full occurrence data
            setDescription(occurrence.description || '')

            setAllocation(occurrence.allocation || '')
            setQuantity(occurrence.quantity?.toString() || '')
            setCause(occurrence.cause || '')
            setCorrection(occurrence.correction || '')
            setRelevant(occurrence.relevant ?? true)
            setStageId(occurrence.stageId || occurrence.Stage?.id || '')

            // Load service info if serviceId exists
            if (occurrence.serviceId) {
                setServiceId(occurrence.serviceId)
                const operationDisplayName = resolveOperationDisplayName(occurrence)
                setSelectedService({
                    id: occurrence.serviceId,
                    name: operationDisplayName || `OP-${occurrence.serviceId}`,
                })
            }

            // Map existing providences
            if (occurrence.providences && occurrence.providences.length > 0) {
                setProvidences(
                    [...occurrence.providences]
                        .sort((a, b) => Number(b.id) - Number(a.id))
                        .map(p => ({
                            id: p.id,
                            description: p.description || '',
                            deadline: p.deadline ? p.deadline.split('T')[0] : '',
                            responsibleId: p.responsibleId || 0,
                            responsibleName: p.responsibleName || '',
                        }))
                )
            }

            // Map existing files
            if (occurrence.FileInOccurrence && occurrence.FileInOccurrence.length > 0) {
                setExistingFiles(
                    occurrence.FileInOccurrence.map(f => ({
                        id: f.GeneralFile.id,
                        name: f.GeneralFile.title,
                        url: f.GeneralFile.fileUrl,
                    }))
                )
            }
        } else {
            // Create mode with pre-fill (e.g. from Production Flow)
            if (occurrence.serviceId) {
                setServiceId(occurrence.serviceId)
                const operationDisplayName = resolveOperationDisplayName(occurrence)
                setSelectedService({
                    id: occurrence.serviceId,
                    name: operationDisplayName || `OP-${occurrence.serviceId}`,
                })
            }
        }
    }, [isEditMode, occurrence])

    const handleClose = () => {
        setOccurrence(null)
        setModalActive(false)
    }

    const handleAddProvidence = () => {
        setProvidences([
            ...providences,
            {
                id: 0,
                description: '',
                deadline: '',
                responsibleId: 0,
                responsibleName: ''
            },
        ])
    }

    const handleDeleteProvidence = (index: number, providence: Providence) => {
        if (providence.id > 0) {
            setProvidenceIdsToDelete([...providenceIdsToDelete, providence.id])
        }
        setProvidences(providences.filter((_, i) => i !== index))
    }

    const handleProvidenceChange = (
        index: number,
        field: keyof Providence,
        value: string | number,
    ) => {
        setProvidences(
            providences.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
        )
    }

    const handleResponsibleChange = (index: number, user: User | null) => {
        setProvidences(
            providences.map((p, i) =>
                i === index
                    ? {
                        ...p,
                        responsibleId: user?.id || 0,
                        responsibleName: user?.name || '',
                    }
                    : p,
            ),
        )
    }

    const handleFileSelect = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.multiple = true
        input.accept = '*/*'
        input.onchange = (e: any) => {
            const files = Array.from(e.target.files || []) as File[]
            const total = selectedFiles.length + files.length
            if (total > 5) {
                setFeedback?.({
                    isOpen: true,
                    statusOk: false,
                    message: 'Máximo de 5 arquivos permitidos',
                })
                return
            }
            setSelectedFiles([...selectedFiles, ...files])
        }
        input.click()
    }

    const handleRemoveSelectedFile = (index: number) => {
        setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
    }

    const handleFileDownload = async (file: { id: number; name: string; url: string }) => {
        try {
            const response = await ApiService.occurrences.downloadFile(occurrence!.id!, file.id)
            const { url } = response.data
            const link = document.createElement('a')
            link.href = url
            link.download = file.name
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Erro ao baixar arquivo:', error)
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Erro ao baixar o arquivo. Tente novamente.',
            })
        }
    }

    const isFormValid = () => {
        return description.trim() !== '' && !!stageId
    }
    const handleSave = async () => {
        if (isSaving) return

        if (!isFormValid()) {
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Por favor, preencha a descrição e selecione uma etapa',
            })
            return
        }

        setIsSaving(true)

        // Build payload — only include non-empty fields
        const payload: any = {
            description,
        }

        if (selectedService?.name) payload.operationNumber = selectedService.name
        if (allocation) payload.allocation = allocation
        if (quantity) payload.quantity = parseInt(quantity) || undefined
        if (cause.trim()) payload.cause = cause
        if (correction.trim()) payload.correction = correction
        payload.relevant = relevant
        if (stageId) payload.stageId = Number(stageId)
        if (serviceId) payload.serviceId = serviceId

        // Providences — send all valid ones (new + existing with potential updates)
        const allProvidences = providences
            .filter(p => p.description.trim() && p.deadline && p.responsibleId)
            .map(p => ({
                ...(p.id > 0 ? { id: p.id } : {}),
                description: p.description,
                deadline: p.deadline,
                responsibleId: p.responsibleId,
            }))

        if (allProvidences.length > 0) {
            payload.providences = allProvidences
        }

        if (isEditMode && providenceIdsToDelete.length > 0) {
            payload.providenceIdsToDelete = providenceIdsToDelete
        }

        try {
            let occurrenceId: number

            if (isEditMode && occurrence?.id) {
                await ApiService.occurrences.update(occurrence.id, payload)
                occurrenceId = occurrence.id
            } else {
                const response = await ApiService.occurrences.create(payload)
                occurrenceId = response.data.id
            }

            // Upload files if any
            if (selectedFiles.length > 0) {
                const formData = new FormData()
                selectedFiles.forEach(file => {
                    formData.append('files', file)
                })
                await ApiService.occurrences.uploadFiles(occurrenceId, formData)
            }

            setFeedback?.({
                isOpen: true,
                statusOk: true,
                message: isEditMode
                    ? 'Ocorrência atualizada com sucesso!'
                    : 'Ocorrência criada com sucesso!',
            })

            setOccurrence(null)
            setModalActive(false)
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Erro desconhecido'
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} ocorrência: ${errorMessage}`,
            })
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Container>
            <HeaderContainer>
                <HeaderTitle>
                    {isEditMode
                        ? `Editar Ocorrência #${occurrence?.id}`
                        : 'Nova Ocorrência'}
                </HeaderTitle>
                <CloseButton onClick={handleClose}>
                    <img src={closeBig} alt='Fechar' width={12} height={12} />
                </CloseButton>
            </HeaderContainer>

            <ContentContainer>
                {/* Row 1: Número da operação + Área */}
                <FormGrid cols={2}>
                    <FormGroup>
                        <SelectAndSearch
                            label='Número da operação'
                            fetchOptions={fetchServices}
                            minChars={2}
                            placeholder='Buscar OP...'
                            selectedOption={selectedService}
                            onSelect={(service) => {
                                setServiceId(service.id)
                                setSelectedService(service)
                            }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Área</Label>
                        <SelectWrapper>
                            <Select
                                value={selectedAreaId}
                                onChange={(e) => {
                                    setSelectedAreaId(e.target.value ? Number(e.target.value) : '')
                                    setSelectedSectorId('')
                                }}
                            >
                                <option value=''>
                                    {loadingAreas ? 'Carregando...' : 'Todas as áreas'}
                                </option>
                                {areas.map(area => (
                                    <option key={area.id} value={area.id}>
                                        {area.name}
                                    </option>
                                ))}
                            </Select>
                            <SelectIcon>
                                <img src={angleDown} alt='Seta' width={18} height={18} />
                            </SelectIcon>
                        </SelectWrapper>
                    </FormGroup>
                </FormGrid>

                {/* Row 2: Setor + Etapa */}
                <FormGrid cols={2}>
                    <FormGroup>
                        <Label>Setor</Label>
                        <SelectWrapper>
                            <Select
                                disabled={!selectedAreaId}
                                value={selectedSectorId}
                                onChange={(e) => setSelectedSectorId(e.target.value ? Number(e.target.value) : '')}
                            >
                                <option value=''>
                                    {!selectedAreaId
                                        ? 'Selecione uma área primeiro'
                                        : loadingSectors
                                            ? 'Carregando...'
                                            : 'Todos os setores'}
                                </option>
                                {sectors.map(sector => (
                                    <option key={sector.id} value={sector.id}>
                                        {sector.name}
                                    </option>
                                ))}
                            </Select>
                            <SelectIcon>
                                <img src={angleDown} alt='Seta' width={18} height={18} />
                            </SelectIcon>
                        </SelectWrapper>
                    </FormGroup>
                    <FormGroup>
                        <Label>Etapa *</Label>
                        <SelectWrapper>
                            <Select
                                value={stageId}
                                onChange={(e) => setStageId(e.target.value ? Number(e.target.value) : '')}
                            >
                                <option value=''>
                                    {loadingStages ? 'Carregando...' : 'Selecione uma etapa'}
                                </option>
                                {filteredStages.map(stage => (
                                    <option key={stage.id} value={stage.id}>
                                        {stage.name}
                                    </option>
                                ))}
                            </Select>
                            <SelectIcon>
                                <img src={angleDown} alt='Seta' width={18} height={18} />
                            </SelectIcon>
                        </SelectWrapper>
                    </FormGroup>
                </FormGrid>

                {/* Row 2: Alocação + Quantidade */}
                <FormGrid cols={2}>
                    <FormGroup>
                        <Label>Alocação</Label>
                        <SelectWrapper>
                            <Select
                                value={allocation}
                                onChange={(e) => setAllocation(e.target.value)}
                            >
                                <option value=''>Selecione</option>
                                <option value='Interno'>Interno</option>
                                <option value='Externo'>Externo</option>
                            </Select>
                            <SelectIcon>
                                <img src={angleDown} alt='Seta' width={18} height={18} />
                            </SelectIcon>
                        </SelectWrapper>
                    </FormGroup>

                    <FormGroup>
                        <Label>Quantidade</Label>
                        <Input
                            type='number'
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            placeholder='Digite aqui'
                        />
                    </FormGroup>
                </FormGrid>

                {/* Descrição (full width) */}
                <FormGroup>
                    <Label>Descrição *</Label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Digite a descrição da ocorrência'
                    />
                </FormGroup>

                {/* Providências */}
                <ActionItemsSection
                    style={{
                        borderTop: '1px solid #ebebeb',
                        borderBottom: '1px solid #ebebeb',
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        marginTop: '16px',
                    }}
                >
                    <ActionItemsHeader>
                        <Label>Responsável</Label>
                        <Label>Descrição</Label>
                        <Label>Prazo</Label>
                        <div></div>
                    </ActionItemsHeader>

                    {providences.map((item, index) => (
                        <ActionItemRow key={item.id || index}>
                            <ResponsibleInputWrapper>
                                <UserSelect<User>
                                    apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user`}
                                    displayField="name"
                                    value={item.responsibleName ? {
                                        id: item.responsibleId,
                                        name: item.responsibleName
                                    } : null}
                                    onChange={(user) => handleResponsibleChange(index, user)}
                                />
                            </ResponsibleInputWrapper>
                            <Input
                                type='text'
                                value={item.description}
                                placeholder='Digite a descrição'
                                onChange={(e) =>
                                    handleProvidenceChange(index, 'description', e.target.value)
                                }
                            />
                            <Input
                                type='date'
                                value={item.deadline}
                                onChange={(e) =>
                                    handleProvidenceChange(index, 'deadline', e.target.value)
                                }
                            />
                            <IconButton
                                type='button'
                                onClick={() => handleDeleteProvidence(index, item)}
                            >
                                <img
                                    src={trash}
                                    alt='Deletar'
                                    width={20}
                                    height={20}
                                />
                            </IconButton>
                        </ActionItemRow>
                    ))}

                    <AddButton type='button' onClick={handleAddProvidence}>
                        <img src={plus} alt='Adicionar' width={12} height={12} />
                        Adicionar providência
                    </AddButton>
                </ActionItemsSection>

                {/* Causa + Pertinente */}
                <Section style={{ paddingTop: '16px' }}>
                    <CausaPertinenteWrapper>
                        <CausaWrapper>
                            <Label>Causa</Label>
                            <Input
                                type='text'
                                value={cause}
                                onChange={(e) => setCause(e.target.value)}
                                placeholder='Digite aqui'
                            />
                        </CausaWrapper>
                        <PertinenteWrapper>
                            <Label>Pertinente</Label>
                            <ToggleSwitchLabel htmlFor='relevant-toggle'>
                                <ToggleSwitchInput
                                    type='checkbox'
                                    id='relevant-toggle'
                                    checked={relevant}
                                    onChange={(e) => setRelevant(e.target.checked)}
                                />
                                <ToggleSwitchSlider checked={relevant}>
                                    <ToggleSwitchThumb checked={relevant}>
                                        {relevant ? (
                                            <img
                                                src={switchCheck}
                                                alt='Check'
                                                style={{ width: '12px', height: '12px' }}
                                            />
                                        ) : (
                                            <img
                                                src={crossLight}
                                                alt='Close'
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    filter: 'brightness(0) invert(1)',
                                                }}
                                            />
                                        )}
                                    </ToggleSwitchThumb>
                                </ToggleSwitchSlider>
                            </ToggleSwitchLabel>
                        </PertinenteWrapper>
                    </CausaPertinenteWrapper>
                </Section>

                {/* Correção + Anexo */}
                <Section>
                    <FormGrid cols={2}>
                        <FormGroup>
                            <Label>Ação corretiva efetiva</Label>
                            <Input
                                value={correction}
                                onChange={(e) => setCorrection(e.target.value)}
                                placeholder='Digite aqui'
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Anexo</Label>
                            <AttachmentContainer onClick={handleFileSelect}>
                                <span>
                                    {selectedFiles.length > 0
                                        ? `${selectedFiles.length} arquivo(s) selecionado(s)`
                                        : 'Clique para adicionar arquivos'}
                                </span>
                                <img src={clip} alt='Anexo' width={18} height={18} />
                            </AttachmentContainer>
                            {/* New files */}
                            {selectedFiles.length > 0 && (
                                <FileList>
                                    {selectedFiles.map((file, index) => (
                                        <FileItem key={index}>
                                            <span>{file.name}</span>
                                            <FileRemoveButton onClick={() => handleRemoveSelectedFile(index)}>
                                                <X size={14} />
                                            </FileRemoveButton>
                                        </FileItem>
                                    ))}
                                </FileList>
                            )}
                            {/* Existing files (edit mode) */}
                            {existingFiles.length > 0 && (
                                <FileList>
                                    {existingFiles.map(file => (
                                        <FileItem key={file.id} style={{ backgroundColor: '#e8f5e9' }}>
                                            <span>{file.name}</span>
                                            <span
                                                onClick={() => handleFileDownload(file)}
                                                style={{ color: '#0090d3', fontSize: '12px', cursor: 'pointer' }}
                                            >
                                                Download
                                            </span>
                                        </FileItem>
                                    ))}
                                </FileList>
                            )}
                        </FormGroup>
                    </FormGrid>
                </Section>
            </ContentContainer>

            <ButtonContainer>
                <CancelButton type='button' onClick={handleClose} disabled={isSaving}>
                    Cancelar
                </CancelButton>
                <SaveButton type='button' onClick={handleSave} disabled={isSaving}>
                    <img src={whitePlus} alt='Salvar' width={12} height={12} />
                    {isSaving
                        ? 'Salvando...'
                        : isEditMode
                            ? 'Salvar alterações'
                            : 'Criar Ocorrência'}
                </SaveButton>
            </ButtonContainer>
        </Container>
    )
}

export default CreateEditOccurrenceModal